// Tour standings: serialisable stage result, then general classification by time or points, points, mountain,
// sprints, rookies, combativity, stage wins. Standings are split by category, like starts by level.
// Results can come from several "universes" (each player rides against their own bots): points are
// computed in each universe then accumulated per rider.
import type { Race } from './race.js';
import { MODES, resolveRules, type ResolvedTeamRules } from './modes.js';
import { stageProfile, CLOCK_MODES, type CatId, type EventDef, type JerseyDef, type JerseyObjective, type JerseyWearers, type StageMode, type StageProfile } from './events.js';
import type { SegmentKind } from './segments.js';

/** A rider's line of a stage result. `team`: the team of a team stage (null otherwise); the team's time and rank are computed from the lines (scoreStage). */
export interface StageRider { key: string; name: string; color: string; human: boolean; cat: string; rookie: boolean; rank: number; time: number | null; dist: number; goal: number | null; out: boolean; hits: number; team?: string | null }
export interface StageCheckpoint { kind: 'sprint' | 'kom'; cat?: number; label?: string; order: string[] }
/**
 * A timed segment of the stage (ADR 0031): each rider's best time through it, fastest first, and the riders in the
 * order they completed it, lap by lap (bounded: the scoring tables stop at fifteen places).
 */
export interface StageSegment { id: string; kind: SegmentKind; label?: string; cat?: number; from: number; to: number; fastest: { key: string; time: number }[]; first: string[][] }
export interface StageResult { stage: number; mode: StageMode | string; elapsed: number; riders: StageRider[]; cps: StageCheckpoint[]; segments?: StageSegment[]; date?: number }
/** Riders kept per segment and per lap in a stage result. */
export const STAGE_SEGMENT_KEEP = { fastest: 64, first: 20, laps: 16 } as const;

/** Snapshot of a finished stage (identical on client and server). */
export function stageResult(race: Race, stage: number): StageResult {
  const res = race.results();
  return {
    stage, mode: race.mode, elapsed: +Math.max(0, race.t).toFixed(2), date: Date.now(),
    riders: res.map(r => ({ key: r.key, name: r.name, color: r.color, human: r.human, cat: r.cat || 'E', rookie: r.rookie, rank: r.rank, time: r.time == null ? null : +r.time.toFixed(2), dist: Math.round(r.dist), goal: r.goal == null ? null : Math.round(r.goal), out: r.out, hits: r.hits, team: r.team })),
    cps: race.cps.map(c => ({ kind: c.kind, cat: c.cat, label: c.label, order: c.order.map(id => race.riders[id]?.key).filter(Boolean) })),
    segments: race.segs.map(g => ({
      id: g.def.id, kind: g.def.kind, label: g.def.name, cat: g.def.cat, from: g.def.from, to: g.def.to,
      fastest: [...g.best].map(([id, time]) => ({ key: race.riders[id]?.key, time })).filter(x => x.key).sort((a, b) => a.time - b.time).slice(0, STAGE_SEGMENT_KEEP.fastest),
      first: g.orders.slice(0, STAGE_SEGMENT_KEEP.laps).map(o => (o ?? []).map(id => race.riders[id]?.key).filter(Boolean).slice(0, STAGE_SEGMENT_KEEP.first)),
    })),
  };
}

// ---------- Scoring tables ----------
export const FINISH_POINTS: Record<StageProfile, number[]> = {
  plat: [50, 30, 20, 18, 16, 14, 12, 10, 8, 7, 6, 5, 4, 3, 2],
  vallonne: [30, 25, 22, 19, 17, 15, 13, 11, 9, 7, 6, 5, 4, 3, 2],
  montagne: [20, 17, 15, 13, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1],
  clm: [20, 17, 15, 13, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1],
};
export const SPRINT_POINTS = [20, 17, 15, 13, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1];
export const KOM_POINTS: number[][] = [[20, 15, 12, 10, 8, 6, 4, 2], [10, 8, 6, 4, 2, 1], [5, 3, 2, 1], [2, 1], [1]];
export const GC_POINTS = [25, 20, 16, 13, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1];
export const FINISH_BONUS = [10, 6, 4], SPRINT_BONUS = [3, 2, 1];
/** Gap assigned per place in modes without a clock (elimination). */
export const RANK_GAP = 10;

/** A rider's line on a stage, within their category. On a team stage, `time` is the team's time and `teamRank` its place. */
export interface StageLine {
  key: string; name: string; color: string; human: boolean; cat: string; rookie: boolean;
  rank: number; time: number; bonus: number; points: number; sprint: number; kom: number; gcPoints: number; hits: number; win: boolean; estimated: boolean;
  team: string | null; teamRank: number | null;
}
/** A team of a team stage, within a category: its place, its time and its riders' lines. */
export interface StageTeam { team: string; rank: number; time: number; riders: StageLine[] }

/** The team rules a stage mode plays by, or null for an individual mode. */
export const stageTeamRules = (mode: StageMode | string): ResolvedTeamRules | null => (mode in MODES ? resolveRules(MODES[mode as StageMode].rules).teams : null);

/**
 * Team scoring of a category's lines: a team's time is its nth rider's (its best or the sum of places for the other
 * scorings is left to the engine's live standings; a stage ranks by time). Every rider takes the team's time, as in
 * a real team time trial, so the general classification needs nothing else; the team's place gives the general
 * classification points and the stage win; finish points are not awarded. Riders without a team ride alone.
 */
function scoreTeams(lines: StageLine[], T: ResolvedTeamRules) {
  const teams = new Map<string, StageLine[]>();
  for (const l of lines) { const k = l.team ?? `solo:${l.key}`; teams.set(k, [...(teams.get(k) ?? []), l]); }
  const timed = [...teams.values()].map(rs => { const times = rs.map(l => l.time).sort((a, b) => a - b); return { rs, time: times[Math.min(T.nth, times.length) - 1] }; });
  timed.sort((a, b) => a.time - b.time);
  timed.forEach(({ rs, time }, i) => { for (const l of rs) { l.time = time; l.teamRank = i + 1; l.gcPoints = GC_POINTS[i] || 0; l.points = 0; l.win = i === 0; } });
  lines.sort((a, b) => a.teamRank! - b.teamRank! || a.rank - b.rank);
  lines.forEach((l, i) => { l.rank = i + 1; });
}

/** The teams of a team stage's lines, by place. Empty for an individual stage. */
export function stageTeams(lines: StageLine[]): StageTeam[] {
  const teams = new Map<string, StageLine[]>();
  for (const l of lines) if (l.team && l.teamRank) teams.set(l.team, [...(teams.get(l.team) ?? []), l]);
  return [...teams.entries()].map(([team, rs]) => ({ team, rank: rs[0].teamRank!, time: rs[0].time, riders: rs })).sort((a, b) => a.rank - b.rank);
}

/** Score of a stage for each rider, category by category. */
export function scoreStage(def: EventDef, r: StageResult): StageLine[] {
  const st = def.stages[r.stage], mode = (st?.mode || r.mode) as StageMode, profile = st ? stageProfile(st) : 'plat';
  const timed = mode === 'race' || CLOCK_MODES.includes(mode), bonuses = def.scoring.bonuses && !CLOCK_MODES.includes(mode) && mode !== 'free';
  const teamRules = stageTeamRules(mode);
  const out: StageLine[] = [];
  const cats = [...new Set(r.riders.map(x => x.cat))];
  for (const cat of cats) {
    const rs = r.riders.filter(x => x.cat === cat).sort((a, b) => a.rank - b.rank);
    const inCat = new Set(rs.map(x => x.key));
    const lines = new Map<string, StageLine>();
    rs.forEach((x, i) => {
      let time = 0, estimated = false;
      if (timed) {
        if (x.time != null) time = x.time;
        else { const v = x.dist > 0 && r.elapsed > 0 ? x.dist / r.elapsed : 8; time = r.elapsed + Math.max(0, (x.goal ?? x.dist) - x.dist) / Math.max(3, v); estimated = true; }
      } else if (mode !== 'free') time = i * RANK_GAP;
      lines.set(x.key, {
        key: x.key, name: x.name, color: x.color, human: x.human, cat, rookie: x.rookie, rank: i + 1, time, bonus: 0,
        points: mode === 'free' ? 0 : FINISH_POINTS[mode === 'elim' ? 'vallonne' : profile][i] || 0,
        sprint: 0, kom: 0, gcPoints: mode === 'free' ? 0 : GC_POINTS[i] || 0, hits: x.hits, win: i === 0 && mode !== 'free', estimated,
        team: x.team ?? null, teamRank: null,
      });
    });
    // an estimated time cannot beat a real time that the rider was ranked behind
    if (timed) { let floor = 0; for (const x of rs) { const l = lines.get(x.key)!; if (l.estimated) l.time = Math.max(l.time, floor + 0.1); floor = Math.max(floor, l.time); } }
    if (teamRules) scoreTeams([...lines.values()], teamRules);
    if (bonuses) rs.slice(0, 3).forEach((x, i) => { lines.get(x.key)!.bonus += FINISH_BONUS[i]; });
    /** Points of a contested place: a sprint feeds the points and sprint tables (with its bonus seconds), a summit the mountain table. */
    const award = (order: string[], objective: 'sprint' | 'mountain', cat: number | undefined, withBonus: boolean) => order.filter(k => inCat.has(k)).forEach((k, i) => {
      const l = lines.get(k)!;
      if (objective === 'sprint') { const p = SPRINT_POINTS[i] || 0; l.points += p; l.sprint += p; if (withBonus && i < 3) l.bonus += SPRINT_BONUS[i]; }
      else l.kom += KOM_POINTS[Math.min(4, Math.max(0, cat ?? 4))][i] || 0;
    });
    for (const c of r.cps) award(c.order, c.kind === 'sprint' ? 'sprint' : 'mountain', c.cat, bonuses);
    // contested segments (ADR 0031): first past the end on every lap, like a sprint line, or fastest through, which earns no bonus seconds
    for (const rule of st?.segments ?? []) {
      const seg = r.segments?.find(x => x.id === rule.id);
      if (!seg) continue;
      if (rule.by === 'first') for (const o of seg.first) award(o, rule.objective, seg.cat, bonuses);
      else award(seg.fastest.map(x => x.key), rule.objective, seg.cat, false);
    }
    out.push(...lines.values());
  }
  return out;
}

// ---------- Cumulative standings ----------
export interface GcLine {
  key: string; name: string; color: string; human: boolean; cat: string; rookie: boolean;
  rank: number; time: number; gap: number; gcPoints: number; points: number; sprint: number; kom: number; hits: number; wins: number;
  stages: number; complete: boolean; jersey: string | null;
}
export interface TableLine { key: string; name: string; color: string; human: boolean; value: number }
export interface JerseyStanding { jersey: JerseyDef; leader: GcLine | null; table: TableLine[] }
export interface CatStandings { cat: string; gc: GcLine[]; jerseys: JerseyStanding[]; stages: { stage: number; lines: StageLine[] }[] }
export interface TourStandings { stagesDone: number; cats: Record<string, CatStandings> }

const VALUE: Record<Exclude<JerseyObjective, 'gc' | 'rookie'>, (l: GcLine) => number> = {
  points: l => l.points, mountain: l => l.kom, sprint: l => l.sprint, combative: l => l.hits, wins: l => l.wins,
};

/**
 * Standings after the supplied stages. `humansOnly` keeps only the players (standings between friends, each having ridden
 * against their own bots). A stage supplied twice for the same rider is counted only once (the first).
 */
export function standings(def: EventDef, results: StageResult[], opts: { humansOnly?: boolean } = {}): TourStandings {
  const byStage = new Map<number, StageLine[]>(), seen = new Set<string>();
  for (const r of [...results].sort((a, b) => a.stage - b.stage)) {
    if (!def.stages[r.stage]) continue;
    for (const l of scoreStage(def, r)) {
      if (opts.humansOnly && !l.human) continue;
      const id = r.stage + '|' + l.key;
      if (seen.has(id)) continue;
      seen.add(id);
      if (!byStage.has(r.stage)) byStage.set(r.stage, []);
      byStage.get(r.stage)!.push(l);
    }
  }
  const stagesDone = byStage.size;
  const cats: Record<string, CatStandings> = {};
  const allCats = new Set([...byStage.values()].flat().map(l => l.cat));
  for (const cat of allCats) {
    const acc = new Map<string, GcLine>();
    const stages: CatStandings['stages'] = [];
    for (const [stage, ls] of [...byStage.entries()].sort((a, b) => a[0] - b[0])) {
      const mine = ls.filter(l => l.cat === cat).sort((a, b) => a.rank - b.rank || a.time - b.time);
      if (!mine.length) continue;
      stages.push({ stage, lines: mine });
      for (const l of mine) {
        const g = acc.get(l.key) || { key: l.key, name: l.name, color: l.color, human: l.human, cat, rookie: l.rookie, rank: 0, time: 0, gap: 0, gcPoints: 0, points: 0, sprint: 0, kom: 0, hits: 0, wins: 0, stages: 0, complete: false, jersey: null };
        g.time += l.time - l.bonus; g.gcPoints += l.gcPoints; g.points += l.points; g.sprint += l.sprint; g.kom += l.kom; g.hits += l.hits; g.wins += l.win ? 1 : 0; g.stages++;
        g.name = l.name; g.color = l.color;
        acc.set(l.key, g);
      }
    }
    const nStages = stages.length;
    const gc = [...acc.values()];
    for (const g of gc) g.complete = g.stages === nStages;
    const byPoints = def.scoring.gc === 'points';
    gc.sort((a, b) => Number(b.complete) - Number(a.complete) || b.stages - a.stages || (byPoints ? b.gcPoints - a.gcPoints || a.time - b.time : a.time - b.time));
    const lead = gc[0];
    gc.forEach((g, i) => { g.rank = i + 1; g.gap = lead ? (byPoints ? lead.gcPoints - g.gcPoints : g.time - lead.time) : 0; });
    const jerseys = def.jerseys.map(j => {
      let table: TableLine[];
      if (j.objective === 'gc') table = gc.map(g => line(g, byPoints ? g.gcPoints : g.time));
      else if (j.objective === 'rookie') table = gc.filter(g => g.rookie).map(g => line(g, byPoints ? g.gcPoints : g.time));
      else { const f = VALUE[j.objective]; table = gc.filter(g => f(g) > 0).sort((a, b) => f(b) - f(a) || a.rank - b.rank).map(g => line(g, f(g))); }
      const leader = table.length ? gc.find(g => g.key === table[0].key) || null : null;
      return { jersey: j, leader, table };
    });
    const cs: CatStandings = { cat, gc, jerseys, stages };
    for (const [key, j] of jerseyWearers(cs)) { const g = gc.find(x => x.key === key); if (g) g.jersey = j.id; }
    cats[cat] = cs;
  }
  return { stagesDone, cats };
}

const line = (g: GcLine, value: number): TableLine => ({ key: g.key, name: g.name, color: g.color, human: g.human, value });

/** Jersey wearing: in priority order, a rider wears only one; if they lead several, the next in the standings wears the other. */
export function jerseyWearers(cs: Pick<CatStandings, 'jerseys'>): JerseyWearers {
  const out: JerseyWearers = new Map();
  for (const js of cs.jerseys) {
    const who = js.table.find(t => !out.has(t.key));
    if (who) out.set(who.key, js.jersey);
  }
  return out;
}

/** Jerseys worn at the start of the next stage, all categories combined. */
export function wearersFor(s: TourStandings | null): JerseyWearers {
  const out: JerseyWearers = new Map();
  if (!s) return out;
  for (const cs of Object.values(s.cats)) for (const [k, j] of jerseyWearers(cs)) out.set(k, j);
  return out;
}

// ---------- The tour so far ----------
/** The general of a category after one stage: who wore which jersey (jersey id → rider key) and every rider's gap to the leader. */
export interface TimelinePoint { stage: number; wearers: Record<string, string>; gc: { key: string; name: string; color: string; human: boolean; rank: number; gap: number }[] }
/** A jersey changing shoulders at the end of a stage; `from` is null when it is first awarded. */
export interface JerseyHandover { stage: number; jersey: string; from: string | null; to: string }

/**
 * The category's standings after each stage ridden, in stage order: the gaps of the general and the jerseys worn at the
 * start of the next stage. Replays `standings` on the results up to each stage, so it tells the story the riders lived.
 */
export function tourTimeline(def: EventDef, results: StageResult[], cat: string, opts: { humansOnly?: boolean } = {}): TimelinePoint[] {
  const stages = [...new Set(results.map(r => r.stage).filter(i => def.stages[i]))].sort((a, b) => a - b);
  return stages.flatMap(stage => {
    const cs = standings(def, results.filter(r => r.stage <= stage), opts).cats[cat];
    if (!cs || !cs.stages.some(s => s.stage === stage)) return [];
    const wearers: Record<string, string> = {};
    for (const [key, j] of jerseyWearers(cs)) wearers[j.id] = key;
    return [{ stage, wearers, gc: cs.gc.map(g => ({ key: g.key, name: g.name, color: g.color, human: g.human, rank: g.rank, gap: g.gap })) }];
  });
}

/** The stages where a jersey changed shoulders, its first award included, in stage order then in the order of the event's jerseys. */
export function jerseyHandovers(def: EventDef, timeline: TimelinePoint[]): JerseyHandover[] {
  const out: JerseyHandover[] = [];
  let prev: Record<string, string> = {};
  for (const p of timeline) {
    for (const j of def.jerseys) { const to = p.wearers[j.id]; if (to && to !== prev[j.id]) out.push({ stage: p.stage, jersey: j.id, from: prev[j.id] ?? null, to }); }
    prev = p.wearers;
  }
  return out;
}

/** Readable gap to the leader: "+1:23" by time, "−12 pts" by points. */
export function fmtGap(g: number, points = false): string {
  if (points) return g ? `−${g} pts` : '';
  if (g <= 0.05) return '';
  // rounded as a whole, then split: rounding the seconds alone showed "+1:60" for 119.6 s
  const whole = Math.round(g), m = Math.floor(whole / 60);
  return `+${m}:${String(whole - m * 60).padStart(2, '0')}`;
}

export const catOf = (s: TourStandings, cat: CatId | string): CatStandings | null => s.cats[cat] || null;
